import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import {
  MapPin, Star, Heart, ChevronLeft, ChevronRight,
  Wifi, Wind, Car, Waves, Shield,
  Users, Check, ChevronDown, Share2, X, Clock,
  Camera, Plane, Globe, CreditCard, Languages,
  BedDouble, ConciergeBell, Dumbbell, UtensilsCrossed,
  Briefcase, Baby, Sparkles, Tv, Coffee, ShieldCheck,
  ParkingSquare, Bath, Bike, Utensils, AirVent
} from "lucide-react";
import { useFavorites } from "../features/useFavorites";

import Navbar from "../../components/Header";
import HotelSEO from "../../components/Hotel/HotelSEO";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

const G       = "#3A9D23";
const G_DARK  = "#2f7d1c";
const G_LIGHT = "#e8f5e2";
const API     = "https://api.komorotel.com";

const KMF_PER_EUR = 491.96775;
const toKMF = (eur) => eur ? Math.round(eur * KMF_PER_EUR).toLocaleString("fr-FR") : null;
const fmtDisplay = d => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
const fmtAPI     = d => new Date(d).toLocaleDateString("sv-SE");

// ─── Catégories équipements (même que le back-office) ────────────────────────
const EQUIP_CATEGORIES = [
  { id: "confort",   label: "Confort & Chambre",  Icon: BedDouble        },
  { id: "tech",      label: "Technologie",         Icon: Wifi             },
  { id: "services",  label: "Services",            Icon: ConciergeBell    },
  { id: "loisirs",   label: "Loisirs & Bien-être", Icon: Dumbbell         },
  { id: "transport", label: "Transport",           Icon: Car              },
  { id: "repas",     label: "Restauration",        Icon: UtensilsCrossed  },
  { id: "business",  label: "Business",            Icon: Briefcase        },
  { id: "famille",   label: "Enfants & Famille",   Icon: Baby             },
  { id: "autre",     label: "Autre",               Icon: Sparkles         },
];

// Icône par nom d'équipement pour l'affichage rapide en header
const EQUIP_NAME_ICONS = {
  "wifi":          <Wifi size={14} />,
  "wi-fi":         <Wifi size={14} />,
  "climatisation": <AirVent size={14} />,
  "télévision":    <Tv size={14} />,
  "tv":            <Tv size={14} />,
  "piscine":       <Waves size={14} />,
  "parking":       <Car size={14} />,
  "restaurant":    <Utensils size={14} />,
  "spa":           <Bath size={14} />,
  "bar":           <Coffee size={14} />,
  "navette":       <Plane size={14} />,
  "vélos":         <Bike size={14} />,
};

function getEquipIcon(name) {
  const lower = name.toLowerCase();
  for (const [key, icon] of Object.entries(EQUIP_NAME_ICONS)) {
    if (lower.includes(key)) return icon;
  }
  return <Check size={14} />;
}

// Normalisation legacy string[] → [{name, category}]
function normalizeEquipments(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map(item =>
    typeof item === "string" ? { name: item, category: "autre" } : item
  );
}

const TYPE_LABELS = {
  hotel:              "Hôtel",
  chambre_hote:       "Chambre d'hôte",
  appartement_studio: "Appartement & Studio",
};

const POLICY_STYLE = {
  free:           { label: "Annulation gratuite",  color: G         },
  partial:        { label: "Annulation partielle", color: "#f59e0b" },
  non_refundable: { label: "Non remboursable",     color: "#ef4444" },
};

const MEAL_LABELS = {
  breakfast:  "Petit-déjeuner inclus",
  half_board: "Demi-pension",
  full_board: "Pension complète",
};

/* ─────────────────────────────────────────────────
   SECTION ÉQUIPEMENTS (nouvelle, premium)
───────────────────────────────────────────────── */
function AmenitiesSection({ equipments }) {
  const [expanded, setExpanded] = useState(false);
  const normalized = normalizeEquipments(equipments);
  if (!normalized.length) return null;

  // Grouper par catégorie
  const grouped = EQUIP_CATEGORIES.reduce((acc, cat) => {
    const items = normalized.filter(e => e.category === cat.id);
    if (items.length > 0) acc.push({ ...cat, items });
    return acc;
  }, []);

  // Nb total de catégories, on affiche 4 max avant expand
  const visibleGroups = expanded ? grouped : grouped.slice(0, 4);
  const hiddenCount   = grouped.length - 4;

  return (
    <div style={{ background: "#fff", borderRadius: 16, padding: "24px", border: "1px solid #f3f4f6", marginBottom: 20 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 20 }}>Équipements & Services</h3>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "20px 32px" }}>
        {visibleGroups.map(cat => {
          const { Icon } = cat;
          return (
            <div key={cat.id}>
              {/* Label catégorie */}
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
                <div style={{
                  width: 26, height: 26, borderRadius: 7,
                  background: G_LIGHT, display: "flex",
                  alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <Icon size={13} color={G} strokeWidth={2.2} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#4a7c59", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                  {cat.label}
                </span>
              </div>
              {/* Items */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {cat.items.map((item, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#374151" }}>
                    <Check size={13} color={G} strokeWidth={2.5} style={{ flexShrink: 0 }} />
                    {item.name}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {grouped.length > 4 && (
        <button
          onClick={() => setExpanded(v => !v)}
          style={{
            marginTop: 20, background: "none", border: `1px solid #e5e7eb`,
            borderRadius: 9, padding: "9px 18px", fontSize: 13, fontWeight: 600,
            color: "#374151", cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            transition: "all 0.18s",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = G; e.currentTarget.style.color = G; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.color = "#374151"; }}
        >
          {expanded
            ? "Voir moins ↑"
            : `Afficher toutes les catégories (${hiddenCount} de plus) ↓`
          }
        </button>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────
   GALERIE
───────────────────────────────────────────────── */
function PhotoGallery({ photos, name }) {
  const [idx,      setIdx]      = useState(0);
  const [lightbox, setLightbox] = useState(null);

  if (!photos?.length) return (
    <div style={{ width: "100%", aspectRatio: "16/9", maxHeight: 420, background: "#f3f4f6", borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 14 }}>
      Aucune photo disponible
    </div>
  );

  return (
    <>
      <div style={{ position: "relative", borderRadius: 16, overflow: "hidden", cursor: "pointer", background: "#111" }}
        onClick={() => setLightbox(idx)}>
        <img
          src={photos[idx]}
          alt={name}
          style={{ width: "100%", height: "clamp(220px, 45vw, 460px)", objectFit: "cover", display: "block", transition: "opacity 0.2s" }}
        />
        <div style={{ position: "absolute", bottom: 14, right: 14, display: "flex", alignItems: "center", gap: 6, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)", color: "#fff", fontSize: 12, fontWeight: 600, padding: "5px 12px", borderRadius: 20 }}>
          <Camera size={13} />{idx + 1} / {photos.length}
        </div>
        {photos.length > 1 && (
          <>
            <button onClick={e => { e.stopPropagation(); setIdx(i => (i - 1 + photos.length) % photos.length); }}
              style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", width: 38, height: 38, borderRadius: "50%", background: "rgba(255,255,255,0.92)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.18)" }}>
              <ChevronLeft size={18} color="#374151" />
            </button>
            <button onClick={e => { e.stopPropagation(); setIdx(i => (i + 1) % photos.length); }}
              style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", width: 38, height: 38, borderRadius: "50%", background: "rgba(255,255,255,0.92)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.18)" }}>
              <ChevronRight size={18} color="#374151" />
            </button>
          </>
        )}
      </div>

      {photos.length > 1 && (
        <div style={{ display: "flex", gap: 8, marginTop: 8, overflowX: "auto", scrollbarWidth: "none" }}>
          {photos.slice(0, 8).map((p, i) => (
            <div key={i} onClick={() => setIdx(i)}
              style={{ flexShrink: 0, width: 72, height: 52, borderRadius: 8, overflow: "hidden", cursor: "pointer", border: `2px solid ${i === idx ? G : "transparent"}`, transition: "border-color 0.15s" }}>
              <img src={p} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
          ))}
        </div>
      )}

      {lightbox !== null && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.93)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)}
            style={{ position: "absolute", top: 18, right: 18, background: "rgba(255,255,255,0.12)", border: "none", borderRadius: "50%", width: 40, height: 40, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={20} color="#fff" />
          </button>
          <button onClick={e => { e.stopPropagation(); setLightbox(l => (l - 1 + photos.length) % photos.length); }}
            style={{ position: "absolute", left: 18, background: "rgba(255,255,255,0.12)", border: "none", borderRadius: "50%", width: 44, height: 44, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ChevronLeft size={22} color="#fff" />
          </button>
          <img src={photos[lightbox]} alt="" style={{ maxHeight: "88vh", maxWidth: "88vw", objectFit: "contain", borderRadius: 10 }} onClick={e => e.stopPropagation()} />
          <button onClick={e => { e.stopPropagation(); setLightbox(l => (l + 1) % photos.length); }}
            style={{ position: "absolute", right: 18, background: "rgba(255,255,255,0.12)", border: "none", borderRadius: "50%", width: 44, height: 44, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ChevronRight size={22} color="#fff" />
          </button>
          <div style={{ position: "absolute", bottom: 18, color: "rgba(255,255,255,0.55)", fontSize: 13 }}>{lightbox + 1} / {photos.length}</div>
        </div>
      )}
    </>
  );
}

/* ─────────────────────────────────────────────────
   SIDEBAR BOOKING
───────────────────────────────────────────────── */
function BookingSidebar({ hotel, roomsRef }) {
  const navigate = useNavigate();
  const location = useLocation();

  const urlParams   = new URLSearchParams(location.search);
  const urlCheckIn  = urlParams.get("checkIn");
  const urlCheckOut = urlParams.get("checkOut");

  const today    = new Date();
  const tomorrow = new Date(); tomorrow.setDate(today.getDate() + 1);

  const initStart = urlCheckIn  ? new Date(urlCheckIn)  : today;
  const initEnd   = urlCheckOut ? new Date(urlCheckOut) : tomorrow;

  const [dateRange,     setDateRange]     = useState([{ startDate: initStart, endDate: initEnd, key: "selection" }]);
  const [showCal,       setShowCal]       = useState(false);
  const [adults,        setAdults]        = useState(parseInt(urlParams.get("adults"))   || 1);
  const [children,      setChildren]      = useState(parseInt(urlParams.get("children")) || 0);
  const [showGuestDrop, setShowGuestDrop] = useState(false);
  const calRef   = useRef();
  const guestRef = useRef();

  useEffect(() => {
    const h = e => {
      if (calRef.current   && !calRef.current.contains(e.target))   setShowCal(false);
      if (guestRef.current && !guestRef.current.contains(e.target)) setShowGuestDrop(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const applyDates = (range, a = adults, c = children) => {
    const p = new URLSearchParams(location.search);
    p.set("checkIn",  fmtAPI(range[0].startDate));
    p.set("checkOut", fmtAPI(range[0].endDate));
    p.set("adults",   a);
    p.set("children", c);
    navigate(`${location.pathname}?${p}`, { replace: true });
  };

  const nights = Math.max(1, Math.round((dateRange[0].endDate - dateRange[0].startDate) / 86400000));

  const CounterRow = ({ label, hint, value, onDec, onInc, min = 0 }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: label === "Adultes" ? 12 : 0 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: "#9ca3af" }}>{hint}</div>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onDec} disabled={value <= min}
          style={{ width: 28, height: 28, borderRadius: "50%", border: `1.5px solid ${value <= min ? "#e5e7eb" : G}`, background: "none", cursor: value <= min ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: value <= min ? "#d1d5db" : G, fontSize: 18 }}>−</button>
        <span style={{ fontSize: 15, fontWeight: 700, minWidth: 20, textAlign: "center" }}>{value}</span>
        <button onClick={onInc}
          style={{ width: 28, height: 28, borderRadius: "50%", border: `1.5px solid ${G}`, background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: G, fontSize: 18 }}>+</button>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb", padding: "22px", boxShadow: "0 4px 24px rgba(0,0,0,0.07)" }}>
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>À partir de</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ fontFamily: "Georgia, serif", fontSize: 34, fontWeight: 700, color: "#111827", lineHeight: 1 }}>{hotel.minPrice}€</span>
            <span style={{ fontSize: 12, color: "#9ca3af", fontWeight: 500 }}>/nuit</span>
          </div>
        </div>
        {hotel.minPrice && (
          <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>≈ {toKMF(hotel.minPrice)} KMF / nuit</div>
        )}
        <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 18 }}>Taxe de séjour incluse</div>

        <div ref={calRef} style={{ position: "relative", marginBottom: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "visible", cursor: "pointer" }}
            onClick={() => setShowCal(!showCal)}>
            <div style={{ padding: "10px 14px", borderRight: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 3 }}>Arrivée</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{fmtDisplay(dateRange[0].startDate)}</div>
            </div>
            <div style={{ padding: "10px 14px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 3 }}>Départ</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{fmtDisplay(dateRange[0].endDate)}</div>
            </div>
          </div>
          {showCal && (
            <div style={{ position: "absolute", top: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)", zIndex: 200, background: "#fff", borderRadius: 14, boxShadow: "0 8px 40px rgba(0,0,0,0.14)", border: "1px solid #f3f4f6", overflow: "hidden" }}>
              <DateRange editableDateInputs onChange={i => setDateRange([i.selection])} moveRangeOnFirstSelection={false} ranges={dateRange} minDate={new Date()} rangeColors={[G]} />
              <div style={{ padding: "0 14px 12px", textAlign: "right" }}>
                <button onClick={() => { setShowCal(false); applyDates(dateRange); }} style={{ background: G, color: "#fff", border: "none", borderRadius: 8, padding: "7px 18px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Confirmer</button>
              </div>
            </div>
          )}
        </div>

        <div ref={guestRef} style={{ position: "relative", marginBottom: 14 }}>
          <div onClick={() => setShowGuestDrop(!showGuestDrop)}
            style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 3 }}>Voyageurs</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                {adults} adulte{adults > 1 ? "s" : ""}
                {children > 0 ? ` · ${children} enfant${children > 1 ? "s" : ""}` : ""}
              </div>
            </div>
            <ChevronDown size={14} color="#9ca3af" style={{ transition: "transform 0.2s", transform: showGuestDrop ? "rotate(180deg)" : "none" }} />
          </div>
          {showGuestDrop && (
            <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", padding: "14px 16px", zIndex: 200, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
              <CounterRow label="Adultes" hint="13 ans et plus" value={adults} min={1}
                onDec={() => { const a = Math.max(1, adults-1); setAdults(a); applyDates(dateRange, a, children); }}
                onInc={() => { const a = adults+1; setAdults(a); applyDates(dateRange, a, children); }} />
              <CounterRow label="Enfants" hint="Moins de 13 ans" value={children} min={0}
                onDec={() => { const c = Math.max(0, children-1); setChildren(c); applyDates(dateRange, adults, c); }}
                onInc={() => { const c = children+1; setChildren(c); applyDates(dateRange, adults, c); }} />
            </div>
          )}
        </div>

        <button onClick={() => { applyDates(dateRange); setTimeout(() => roomsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
          style={{ width: "100%", background: G, color: "#fff", border: "none", borderRadius: 11, padding: "14px 0", fontSize: 14, fontWeight: 700, cursor: "pointer", transition: "background 0.18s", marginBottom: 10 }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = G_DARK}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = G}>
          Voir les disponibilités
        </button>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 12, color: "#6b7280" }}>
          <Shield size={13} color={G} />Aucun paiement aujourd'hui
        </div>

        {hotel.minPrice && (
          <div style={{ borderTop: "1px solid #f3f4f6", marginTop: 16, paddingTop: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#6b7280", marginBottom: 6 }}>
              <span>{hotel.minPrice}€ × {nights} nuit{nights > 1 ? "s" : ""}</span>
              <span>{hotel.minPrice * nights}€</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700, color: "#111827" }}>
              <span>Total estimé</span><span>{hotel.minPrice * nights}€</span>
            </div>
            <div style={{ textAlign: "right", fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
              ≈ {toKMF(hotel.minPrice * nights)} KMF
            </div>
          </div>
        )}
      </div>

      <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <iframe title="map" width="100%" height="160" frameBorder="0" style={{ display: "block", border: 0 }}
          src={`https://maps.google.com/maps?q=${encodeURIComponent((hotel.city || "") + ", Comores")}&z=13&output=embed`} allowFullScreen />
        <div style={{ padding: "12px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <MapPin size={13} color="#9ca3af" />
            <span style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>{hotel.city}{hotel.island ? `, ${hotel.island}` : ""}, Comores</span>
          </div>
          <a href={`https://maps.google.com/?q=${encodeURIComponent((hotel.city || "") + " Comores")}`} target="_blank" rel="noreferrer"
            style={{ fontSize: 12, color: G, fontWeight: 600, textDecoration: "none" }}>
            Voir sur la carte →
          </a>
          
        </div>
      </div>

      <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb", padding: "18px" }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Informations pratiques</h4>
        {[
          { icon: <Clock size={13} color="#9ca3af"/>,      label: "Check-in",   val: hotel.checkIn  || "14:00 – 22:00" },
          { icon: <Clock size={13} color="#9ca3af"/>,      label: "Check-out",  val: hotel.checkOut || "06:00 – 11:00" },
          { icon: <Shield size={13} color="#9ca3af"/>,     label: "Annulation", val: "Varie selon le tarif" },
          { icon: <CreditCard size={13} color="#9ca3af"/>, label: "Paiement",   val: hotel.paymentMethods?.join(", ") || "Visa, Mastercard, Cash" },
          { icon: <Languages size={13} color="#9ca3af"/>,  label: "Langues",    val: hotel.languages?.join(", ") || "Français, Anglais, Arabe" },
        ].map((row, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: i < 4 ? 12 : 0 }}>
            <div style={{ marginTop: 1, flexShrink: 0 }}>{row.icon}</div>
            <span style={{ fontSize: 12, color: "#6b7280", minWidth: 70, flexShrink: 0 }}>{row.label}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#374151", flex: 1 }}>{row.val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   SECTION CHAMBRES
───────────────────────────────────────────────── */
function RoomsSection({ hotelId, roomsRef, slug, onOpenDates }) {
  const [rooms,   setRooms]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const params   = new URLSearchParams(location.search);
  const checkIn  = params.get("checkIn");
  const checkOut = params.get("checkOut");
  const adults   = params.get("adults")   || 1;
  const children = params.get("children") || 0;

  useEffect(() => {
    if (!hotelId) return;
    setLoading(true);
    const url = checkIn && checkOut
      ? `${API}/api/rateprices/hotel/${hotelId}?checkIn=${checkIn}&checkOut=${checkOut}&adults=${adults}&children=${children}`
      : `${API}/api/rooms/hotel/${hotelId}`;
    fetch(url)
      .then(r => r.json())
      .then(d => setRooms(Array.isArray(d) ? d : []))
      .catch(() => setRooms([]))
      .finally(() => setLoading(false));
  }, [hotelId, checkIn, checkOut, adults, children]);

  const displayed = showAll ? rooms : rooms.slice(0, 3);

  return (
    <div ref={roomsRef} style={{ scrollMarginTop: 96 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: "#111827" }}>Chambres disponibles</h2>
      </div>

      {loading ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
          {[...Array(3)].map((_, i) => (
            <div key={i} style={{ height: 260, borderRadius: 12, background: "linear-gradient(90deg,#f3f4f6 25%,#e9eaec 50%,#f3f4f6 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
          ))}
        </div>
      ) : rooms.length === 0 ? (
        <div style={{ padding: "40px 0", textAlign: "center", color: "#6b7280", fontSize: 14 }}>
          {checkIn && checkOut ? "Aucune chambre disponible pour ces dates." : "Sélectionnez des dates pour voir les disponibilités."}
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {displayed.map((room, i) => (
              <RoomAccordion
                key={room.roomId || room._id || i}
                room={room}
                checkIn={checkIn}
                checkOut={checkOut}
                navigate={navigate}
                slug={slug}
                roomsRef={roomsRef}
                onOpenDates={onOpenDates}
              />
            ))}
          </div>
          {rooms.length > 3 && (
            <button onClick={() => setShowAll(!showAll)}
              style={{ marginTop: 16, background: "none", border: `1px solid ${G}`, color: G, borderRadius: 9, padding: "10px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              {showAll ? "Voir moins ↑" : `Voir toutes les chambres (${rooms.length}) ↓`}
            </button>
          )}
        </>
      )}
      <style>{`@keyframes shimmer{to{background-position:-200% 0}}`}</style>
    </div>
  );
}

function RoomAccordion({ room, checkIn, checkOut, navigate, slug, roomsRef, onOpenDates }) {
  const [open,     setOpen]     = useState(false);
  const [selected, setSelected] = useState(0);
  const [hovered,  setHovered]  = useState(false);

  const photo     = room.photos?.[0];
  const prices    = room.prices || [];
  const hasDates  = !!(checkIn && checkOut);
  const bestPrice = prices[0];

  const handleBook = () => {
    const p = new URLSearchParams();
    if (checkIn)  p.set("checkIn",  checkIn);
    if (checkOut) p.set("checkOut", checkOut);
    p.set("roomId", room.roomId || room._id);
    const plan = prices[selected];
    if (plan?.ratePlanId) p.set("ratePlanId", plan.ratePlanId);
    const urlP = new URLSearchParams(window.location.search);
    p.set("adults",   urlP.get("adults")   || 1);
    p.set("children", urlP.get("children") || 0);
    navigate(`/checkout?${p}`);
  };

  return (
    <div style={{
      border: `1.5px solid ${hovered && !open ? "#d1d5db" : open ? G : "#f3f4f6"}`,
      borderRadius: 16, background: "#fff", overflow: "hidden",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: open ? `0 0 0 3px ${G}18` : hovered ? "0 4px 16px rgba(0,0,0,0.07)" : "none",
    }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>

      <div style={{ display: "flex", gap: 16, padding: "16px 18px", cursor: "pointer" }}
        onClick={() => hasDates && setOpen(o => !o)}>
        <div style={{ width: 120, height: 88, borderRadius: 10, overflow: "hidden", flexShrink: 0, background: "#f3f4f6" }}>
          {photo
            ? <img src={photo} alt={room.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#d1d5db", fontSize: 11 }}>Photo</div>
          }
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 5 }}>{room.title}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 5, flexWrap: "wrap" }}>
            {room.maxPeople && (
              <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 12, color: "#6b7280" }}>
                <Users size={11} color="#9ca3af" /> {room.maxPeople} pers. max
              </span>
            )}
          </div>
          {room.desc && (
            <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.55, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {room.desc}
            </div>
          )}
          {hasDates && !open && bestPrice && (
            <div style={{ marginTop: 8 }}>
              {POLICY_STYLE[bestPrice.cancellationPolicy?.type] && (
                <span style={{ fontSize: 11, fontWeight: 600, color: POLICY_STYLE[bestPrice.cancellationPolicy.type].color, display: "flex", alignItems: "center", gap: 3 }}>
                  <Check size={10} color={POLICY_STYLE[bestPrice.cancellationPolicy.type].color} />
                  {POLICY_STYLE[bestPrice.cancellationPolicy.type].label}
                </span>
              )}
            </div>
          )}
        </div>
        <div style={{ textAlign: "right", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "space-between" }}>
          <div>
            {hasDates && bestPrice ? (
              <>
                <div style={{ fontSize: 11, color: "#9ca3af" }}>À partir de</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", lineHeight: 1.1 }}>{bestPrice.pricePerNight}€</div>
                <div style={{ fontSize: 11, color: "#9ca3af" }}>/nuit</div>
                {bestPrice.pricePerNightKMF && (
                  <div style={{ fontSize: 10, color: "#9ca3af" }}>≈ {bestPrice.pricePerNightKMF?.toLocaleString("fr-FR")} KMF</div>
                )}
                {bestPrice.totalNights && (
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                    {bestPrice.total}€ · {bestPrice.totalNights} nuit{bestPrice.totalNights > 1 ? "s" : ""}
                  </div>
                )}
              </>
            ) : room.minPrice ? (
              <>
                <div style={{ fontSize: 11, color: "#9ca3af" }}>À partir de</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", lineHeight: 1.1 }}>{room.minPrice}€</div>
                <div style={{ fontSize: 11, color: "#9ca3af" }}>/nuit</div>
                {room.minPriceKMF && (
                  <div style={{ fontSize: 10, color: "#9ca3af" }}>≈ {room.minPriceKMF?.toLocaleString("fr-FR")} KMF</div>
                )}
              </>
            ) : null}
          </div>
          {hasDates ? (
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 8, color: open ? G : "#374151", fontSize: 12, fontWeight: 600 }}>
              {open ? "Masquer" : `${prices.length} tarif${prices.length > 1 ? "s" : ""}`}
              <ChevronDown size={14} style={{ transition: "transform 0.25s", transform: open ? "rotate(180deg)" : "none" }} />
            </div>
          ) : (
            <button
              onClick={e => {
                e.stopPropagation();
                if (window.innerWidth <= 900 && onOpenDates) onOpenDates();
                else roomsRef?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              style={{ marginTop: 10, background: G, color: "#fff", border: "none", borderRadius: 9, padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = G_DARK}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = G}>
              Choisir
            </button>
          )}
        </div>
      </div>

      {hasDates && open && prices.length > 0 && (
        <div style={{ borderTop: "1px solid #f3f4f6" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 18px", background: "#f9fafb" }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.07em", textTransform: "uppercase" }}>Tarif</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.07em", textTransform: "uppercase" }}>Prix/nuit</span>
          </div>
          {prices.map((p, i) => {
            const pol  = POLICY_STYLE[p.cancellationPolicy?.type];
            const meal = MEAL_LABELS[p.mealPlan];
            const isSelected = selected === i;
            return (
              <div key={i} onClick={() => setSelected(i)}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "14px 18px", cursor: "pointer", borderBottom: i < prices.length - 1 ? "1px solid #f9fafb" : "none", background: isSelected ? `${G}08` : "#fff", transition: "background 0.15s", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <div style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${isSelected ? G : "#d1d5db"}`, background: isSelected ? G : "#fff", flexShrink: 0, marginTop: 1, display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}>
                    {isSelected && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginBottom: 4 }}>{p.ratePlanName}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      {pol && <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 600, color: pol.color }}><Check size={10} color={pol.color} />{pol.label}</span>}
                      {meal && <span style={{ fontSize: 11, color: "#6b7280", background: "#f3f4f6", padding: "2px 8px", borderRadius: 4 }}>{meal}</span>}
                    </div>
                    {p.total && p.totalNights && (
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>
                        Total : {p.total}€{p.totalKMF ? ` · ${p.totalKMF?.toLocaleString("fr-FR")} KMF` : ""} · {p.totalNights} nuit{p.totalNights > 1 ? "s" : ""}
                      </div>
                    )}

                    {/* ✅ AJOUTER */}
                    {p.taxConfig?.enabled && (
                      <div style={{ fontSize: 11, marginTop: 2 }}>
                        {p.taxConfig.included
                          ? <span style={{ color: G, fontWeight: 600 }}>✓ Taxe de séjour incluse</span>
                          : <span style={{ color: "#6b7280" }}>+ {p.taxTotal?.toFixed(2)}€ taxe de séjour</span>
                        }
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: "#111827" }}>{p.pricePerNight}€</div>
                  <div style={{ fontSize: 10, color: "#9ca3af" }}>/nuit</div>
                  {p.pricePerNightKMF && <div style={{ fontSize: 10, color: "#9ca3af" }}>{p.pricePerNightKMF?.toLocaleString("fr-FR")} KMF</div>}
                </div>
              </div>
            );
          })}
          <div style={{ padding: "14px 18px", background: `${G}06`, borderTop: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Tarif sélectionné : <strong style={{ color: "#111827" }}>{prices[selected]?.ratePlanName}</strong>
            </div>
            <button onClick={handleBook}
              style={{ background: G, color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "background 0.18s" }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = G_DARK}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = G}>
              Choisir ce tarif →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────
   SECTION AVIS
───────────────────────────────────────────────── */
function ReviewsSection({ hotelId }) {
  const [data,    setData]    = useState(null);
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hotelId) return;
    fetch(`${API}/api/reviews/${hotelId}?page=${page}`)
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [hotelId, page]);

  if (loading || !data?.totalReviews) return null;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>Note des voyageurs</h3>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 24, alignItems: "center", marginBottom: 24 }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 44, fontWeight: 700, color: "#111827", lineHeight: 1 }}>{data.avgRating}</div>
          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>/5 · Excellent</div>
          <div style={{ fontSize: 11, color: "#9ca3af" }}>Basé sur {data.totalReviews} avis</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {["Propreté", "Confort", "Services", "Emplacement", "Rapport qualité/prix"].map((label, i) => {
            const val = (data.avgRating - 0.1 * i).toFixed(1);
            const pct = (parseFloat(val) / 5) * 100;
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, color: "#374151", width: 100, flexShrink: 0 }}>{label}</span>
                <div style={{ flex: 1, height: 6, background: "#f3f4f6", borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${pct}%`, background: G, borderRadius: 3 }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#374151", width: 28, textAlign: "right" }}>{val}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {data.reviews?.slice(0, 4).map((r, i) => (
          <div key={i} style={{ padding: "14px 16px", border: "1px solid #f3f4f6", borderRadius: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <div style={{ width: 34, height: 34, borderRadius: "50%", background: G_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: G, flexShrink: 0 }}>
                {r.userId?.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{r.userId?.name} {r.userId?.surname}</div>
                <div style={{ fontSize: 11, color: "#9ca3af" }}>{new Date(r.createdAt).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</div>
              </div>
              <div style={{ display: "flex", gap: 2 }}>
                {[...Array(5)].map((_, s) => <Star key={s} size={12} fill={s < r.rating ? "#f59e0b" : "#e5e7eb"} color={s < r.rating ? "#f59e0b" : "#e5e7eb"} />)}
              </div>
            </div>
            {r.comment && <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.65, margin: 0 }}>{r.comment}</p>}
            {r.hotelReply?.text && (
              <div style={{ marginTop: 10, padding: "10px 14px", background: "#f9fafb", borderRadius: 8, borderLeft: `3px solid ${G}` }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: G, marginBottom: 4 }}>Réponse de l'établissement</div>
                <p style={{ fontSize: 12, color: "#374151", margin: 0, lineHeight: 1.6 }}>{r.hotelReply.text}</p>
              </div>
            )}
          </div>
        ))}
      </div>
      {data.totalPages > 1 && (
        <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "center" }}>
          {[...Array(data.totalPages)].map((_, i) => (
            <button key={i} onClick={() => setPage(i+1)}
              style={{ width: 32, height: 32, borderRadius: "50%", border: `1px solid ${page === i+1 ? G : "#e5e7eb"}`, background: page === i+1 ? G : "#fff", color: page === i+1 ? "#fff" : "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              {i+1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────
   HÔTELS SIMILAIRES
───────────────────────────────────────────────── */
function SimilarHotels({ currentId, island }) {
  const [hotels, setHotels] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!island) return;
    fetch(`${API}/api/hotels/search?destination=${encodeURIComponent(island)}`)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setHotels(d.filter(h => h._id !== currentId && h.photos?.[0] && h.minPrice).slice(0, 5)); })
      .catch(() => {});
  }, [island, currentId]);

  if (!hotels.length) return null;

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 20 }}>Vous pourriez aussi aimer</h2>
      <div style={{ display: "flex", gap: 16, overflowX: "auto", scrollbarWidth: "none", paddingBottom: 4 }}>
        {hotels.map(h => (
          <div key={h._id}
            style={{ flex: "0 0 200px", borderRadius: 12, overflow: "hidden", background: "#fff", border: "1px solid #f3f4f6", cursor: "pointer", transition: "box-shadow 0.2s" }}
            onClick={() => navigate(`/hotel/slug/${h.slug}`)}
            onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.1)"}
            onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
            <div style={{ position: "relative", height: 130, overflow: "hidden" }}>
              <img src={h.photos[0]} alt={h.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              <div style={{ position: "absolute", bottom: 8, left: 8, background: "rgba(0,0,0,0.52)", backdropFilter: "blur(4px)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 5 }}>
                {h.minPrice}€ <span style={{ opacity: 0.8, fontWeight: 400 }}>/nuit</span>
              </div>
            </div>
            <div style={{ padding: "10px 12px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#111827", marginBottom: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{h.name}</div>
              <div style={{ fontSize: 11, color: "#9ca3af", display: "flex", alignItems: "center", gap: 3 }}>
                <MapPin size={10} color="#9ca3af" />{h.city}
              </div>
              {h.rating && (
                <div style={{ display: "flex", alignItems: "center", gap: 3, marginTop: 5 }}>
                  <Star size={10} fill="#f59e0b" color="#f59e0b" />
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#374151" }}>{h.rating}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   SHARE BUTTON
───────────────────────────────────────────────── */
function ShareButton({ hotel }) {
  const [showFallback, setShowFallback] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setShowFallback(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const shareData = {
    title: `${hotel.name} — Komorotel`,
    text:  `Découvrez ${hotel.name} à ${hotel.city}${hotel.island ? `, ${hotel.island}` : ""}. À partir de ${hotel.minPrice}€/nuit.`,
    url:   `https://komorotel.com/hotel/slug/${hotel.slug}`,
  };

  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share(shareData); } catch (e) {}
    } else {
      setShowFallback(f => !f);
    }
  };

  const copyLink = () => { navigator.clipboard.writeText(shareData.url); setShowFallback(false); };

  const SHARE_OPTIONS = [
    { label: "WhatsApp", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>, href: `https://wa.me/?text=${encodeURIComponent(shareData.text + " " + shareData.url)}` },
    { label: "Facebook", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareData.url)}` },
    { label: "Copier le lien", icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>, onClick: copyLink },
  ];

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={handleShare}
        style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "1px solid #e5e7eb", borderRadius: 8, padding: "6px 12px", fontSize: 13, fontWeight: 500, color: "#374151", cursor: "pointer" }}>
        <Share2 size={13} /> Partager
      </button>
      {showFallback && (
        <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, background: "#fff", borderRadius: 12, border: "1px solid #f3f4f6", boxShadow: "0 8px 32px rgba(0,0,0,0.12)", zIndex: 200, minWidth: 180, overflow: "hidden" }}>
          {SHARE_OPTIONS.map((opt, i) => (
            opt.onClick ? (
              <button key={i} onClick={opt.onClick}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "11px 16px", background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 500, color: "#374151", borderBottom: i < SHARE_OPTIONS.length - 1 ? "1px solid #f9fafb" : "none" }}
                onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                onMouseLeave={e => e.currentTarget.style.background = "none"}>
                {opt.icon}{opt.label}
              </button>
            ) : (
              <a key={i} href={opt.href} target="_blank" rel="noreferrer"
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 16px", textDecoration: "none", fontSize: 13, fontWeight: 500, color: "#374151", borderBottom: i < SHARE_OPTIONS.length - 1 ? "1px solid #f9fafb" : "none" }}
                onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                onMouseLeave={e => e.currentTarget.style.background = "none"}>
                {opt.icon}{opt.label}
              </a>
            )
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────
   MOBILE DATE PICKER
───────────────────────────────────────────────── */
function MobileDatePicker({ hotel, onClose, roomsRef }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const urlParams = new URLSearchParams(location.search);

  const today    = new Date();
  const tomorrow = new Date(); tomorrow.setDate(today.getDate() + 1);
  const initStart = urlParams.get("checkIn")  ? new Date(urlParams.get("checkIn"))  : today;
  const initEnd   = urlParams.get("checkOut") ? new Date(urlParams.get("checkOut")) : tomorrow;

  const [dateRange, setDateRange] = useState([{ startDate: initStart, endDate: initEnd, key: "selection" }]);
  const [adults,    setAdults]    = useState(parseInt(urlParams.get("adults"))   || 1);
  const [children,  setChildren]  = useState(parseInt(urlParams.get("children")) || 0);

  const apply = () => {
    const p = new URLSearchParams(location.search);
    p.set("checkIn",  fmtAPI(dateRange[0].startDate));
    p.set("checkOut", fmtAPI(dateRange[0].endDate));
    p.set("adults",   adults);
    p.set("children", children);
    navigate(`${location.pathname}?${p}`, { replace: true });
    onClose();
    setTimeout(() => roomsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
  };

  const nights = Math.max(1, Math.round((dateRange[0].endDate - dateRange[0].startDate) / 86400000));

  const CounterRow = ({ label, hint, value, onDec, onInc, min = 0 }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: label === "Adultes" ? 12 : 0 }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: "#9ca3af" }}>{hint}</div>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <button onClick={onDec} disabled={value <= min}
          style={{ width: 32, height: 32, borderRadius: "50%", border: `1.5px solid ${value <= min ? "#e5e7eb" : G}`, background: "none", cursor: value <= min ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: value <= min ? "#d1d5db" : G, fontSize: 18 }}>−</button>
        <span style={{ fontSize: 16, fontWeight: 700, color: "#111827", minWidth: 20, textAlign: "center" }}>{value}</span>
        <button onClick={onInc}
          style={{ width: 32, height: 32, borderRadius: "50%", border: `1.5px solid ${G}`, background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: G, fontSize: 18 }}>+</button>
      </div>
    </div>
  );

  return (
    <div style={{ padding: "0 16px 0" }}>
      <div style={{ display: "flex", justifyContent: "center", overflow: "hidden", borderRadius: 12, maxWidth: "100%" }}>
        <style>{`.rdrCalendarWrapper { max-width: 100% !important; } .rdrMonth { width: 100% !important; min-width: 0 !important; }`}</style>
        <DateRange editableDateInputs onChange={i => setDateRange([i.selection])} moveRangeOnFirstSelection={false} ranges={dateRange} minDate={new Date()} rangeColors={[G]} months={1} direction="horizontal" showMonthAndYearPickers />
      </div>
      <div style={{ padding: "12px 4px", borderTop: "1px solid #f3f4f6", marginTop: 8 }}>
        <CounterRow label="Adultes" hint="13 ans et plus" value={adults} min={1} onDec={() => setAdults(a => Math.max(1, a-1))} onInc={() => setAdults(a => a+1)} />
        <CounterRow label="Enfants" hint="Moins de 13 ans" value={children} min={0} onDec={() => setChildren(c => Math.max(0, c-1))} onInc={() => setChildren(c => c+1)} />
      </div>
      <button onClick={apply}
        style={{ width: "100%", background: G, color: "#fff", border: "none", borderRadius: 12, padding: "14px 0", fontSize: 14, fontWeight: 700, cursor: "pointer", marginTop: 8 }}
        onMouseEnter={e => e.currentTarget.style.backgroundColor = G_DARK}
        onMouseLeave={e => e.currentTarget.style.backgroundColor = G}>
        Voir les disponibilités · {nights} nuit{nights > 1 ? "s" : ""}
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────────
   PAGE PRINCIPALE
───────────────────────────────────────────────── */
export default function HotelPage() {
  const { slug }  = useParams();
  const navigate  = useNavigate();
  const roomsRef  = useRef(null);

  const [hotel,           setHotel]           = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [descExpanded,    setDescExpanded]     = useState(false);
  const [mobileDatesOpen, setMobileDatesOpen] = useState(false);

  const { isFavorite, toggleFavorite } = useFavorites();
  const liked = isFavorite(hotel?._id);

  const _loc      = useLocation();
  const _urlP     = new URLSearchParams(_loc.search);
  const urlCheckIn  = _urlP.get("checkIn");
  const urlCheckOut = _urlP.get("checkOut");

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/api/hotels/slug/${slug}`)
      .then(r => r.json())
      .then(d => setHotel(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#f9fafb", fontFamily: "'DM Sans','Inter',system-ui,sans-serif" }}>
      <Navbar />
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "88px 32px 48px" }}>
        {[{ w: "100%", h: 420 }, { w: "60%", h: 28 }, { w: "40%", h: 18 }, { w: "100%", h: 200 }].map((s, i) => (
          <div key={i} style={{ height: s.h, width: s.w, borderRadius: 12, background: "linear-gradient(90deg,#f3f4f6 25%,#e9eaec 50%,#f3f4f6 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite", marginBottom: 16 }} />
        ))}
      </div>
      <style>{`@keyframes shimmer{to{background-position:-200% 0}}`}</style>
    </div>
  );

  if (!hotel) return (
    <div style={{ minHeight: "100vh", background: "#f9fafb", fontFamily: "'DM Sans','Inter',system-ui,sans-serif" }}>
      <Navbar />
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "120px 32px", textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>😕</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 8 }}>Hôtel introuvable</h2>
        <button onClick={() => navigate("/hotels")} style={{ background: G, color: "#fff", border: "none", borderRadius: 10, padding: "11px 24px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          Voir tous les hébergements
        </button>
      </div>
    </div>
  );

  // ── Normalisation équipements ──
  const equipList   = normalizeEquipments(hotel.equipments || []);
  // Top 5 équipements pour le header (pills rapides)
  const topEquips   = equipList.slice(0, 5);
  const needsMore   = hotel.desc?.length > 300;
  const shortDesc   = hotel.desc?.slice(0, 300);

  return (
    <div style={{ minHeight: "100vh", background: "#f9fafb", fontFamily: "'DM Sans','Inter',system-ui,sans-serif", overflowX: "hidden", maxWidth: "100vw" }}>
      <HotelSEO data={hotel} avgRating={hotel.rating} totalReviews={hotel.reviewCount} />
      <Navbar />

      <div className="hotel-container" style={{ maxWidth: 1200, margin: "0 auto", padding: "80px 32px 0", boxSizing: "border-box", width: "100%" }}>

        {/* ── Breadcrumb ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#6b7280", flexWrap: "wrap", overflow: "hidden" }}>
            <Link to="/" style={{ color: "#6b7280", textDecoration: "none" }}>Accueil</Link>
            <ChevronRight size={13} />
            <Link to="/hotels" style={{ color: "#6b7280", textDecoration: "none" }}>{hotel.island || "Comores"}</Link>
            {hotel.city && <><ChevronRight size={13} /><span>{hotel.city}</span></>}
            <ChevronRight size={13} />
            <span style={{ color: "#111827", fontWeight: 500 }}>{hotel.name}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <ShareButton hotel={hotel} />
            <button onClick={() => toggleFavorite(hotel?._id)}
              style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "1px solid #e5e7eb", borderRadius: 8, padding: "6px 12px", fontSize: 13, fontWeight: 500, color: liked ? "#ef4444" : "#374151", cursor: "pointer" }}>
              <Heart size={13} fill={liked ? "#ef4444" : "none"} color={liked ? "#ef4444" : "#374151"} />
              {liked ? "Sauvegardé" : "Ajouter aux favoris"}
            </button>
          </div>
        </div>

        {/* ── Layout principal ── */}
        <div className="hotel-layout" style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 28, alignItems: "start", marginBottom: 32 }}>

          {/* Colonne gauche */}
          <div style={{ minWidth: 0, overflow: "hidden" }}>

            {/* Galerie */}
            <div style={{ marginBottom: 22 }}>
              <PhotoGallery photos={hotel.photos} name={hotel.name} />
            </div>

            {/* Info principale */}
            <div style={{ background: "#fff", borderRadius: 16, padding: "22px 24px", border: "1px solid #f3f4f6", marginBottom: 20, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 6 }}>
                <h1 style={{ fontFamily: "Georgia, serif", fontSize: 26, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>
                  {hotel.name}
                  {hotel.verified && (
                    <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginLeft: 8, verticalAlign: "middle" }}>
                      <circle cx="12" cy="12" r="11" fill={G} opacity="0.15"/>
                      <path d="M9 12l2 2 4-4" stroke={G} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                  )}
                </h1>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, color: "#6b7280", marginBottom: 4 }}>
                <MapPin size={13} color="#9ca3af" />
                {hotel.city}{hotel.island ? `, ${hotel.island}` : ""}, Comores
              </div>
              
              {hotel.rating && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
                  <div style={{ display: "flex", gap: 2 }}>
                    {[...Array(5)].map((_, i) => <Star key={i} size={15} fill={i < Math.round(hotel.rating) ? "#f59e0b" : "#e5e7eb"} color={i < Math.round(hotel.rating) ? "#f59e0b" : "#e5e7eb"} />)}
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{hotel.rating}</span>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>({hotel.reviewCount || "—"} avis)</span>
                  <span style={{ background: G_LIGHT, color: G, fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20 }}>Excellent</span>
                </div>
              )}

              {/* Top 5 équipements — pills rapides en header */}
              {topEquips.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, paddingTop: 16, borderTop: "1px solid #f9fafb" }}>
                  {topEquips.map((eq, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#374151", background: "#f9fafb", borderRadius: 20, padding: "5px 12px" }}>
                      <span style={{ color: G, display: "flex" }}>{getEquipIcon(eq.name)}</span>
                      {eq.name}
                    </div>
                  ))}
                  {equipList.length > 5 && (
                    <div style={{ fontSize: 12, color: "#9ca3af", display: "flex", alignItems: "center", padding: "5px 8px" }}>
                      +{equipList.length - 5} autres
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Grille 3 colonnes */}
            <div className="hotel-info-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 20 }}>
              <div style={{ background: "#fff", borderRadius: 14, padding: "18px 18px", border: "1px solid #f3f4f6" }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 10 }}>À propos</h3>
                {hotel.desc && (
                  <>
                    <p style={{ fontSize: 12, color: "#374151", lineHeight: 1.7, margin: 0 }}>
                      {descExpanded || !needsMore ? hotel.desc : shortDesc + "..."}
                    </p>
                    {needsMore && (
                      <button onClick={() => setDescExpanded(!descExpanded)}
                        style={{ background: "none", border: "none", color: G, fontSize: 12, fontWeight: 700, cursor: "pointer", marginTop: 8, padding: 0, display: "flex", alignItems: "center", gap: 3 }}>
                        {descExpanded ? "Voir moins ↑" : "Voir plus ↓"}
                      </button>
                    )}
                  </>
                )}
              </div>
              <div style={{ background: "#fff", borderRadius: 14, padding: "18px 18px", border: "1px solid #f3f4f6" }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 10 }}>Note des voyageurs</h3>
                {hotel.rating ? (
                  <>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 2 }}>
                      <span style={{ fontFamily: "Georgia, serif", fontSize: 36, fontWeight: 700, color: "#111827" }}>{hotel.rating}</span>
                      <span style={{ fontSize: 13, color: "#9ca3af" }}>/5</span>
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: G, marginBottom: 10 }}>Excellent</div>
                    {["Propreté","Confort","Services","Emplacement"].map((label, i) => {
                      const val = Math.max(0, hotel.rating - 0.1 * i).toFixed(1);
                      return (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                          <span style={{ fontSize: 11, color: "#6b7280", width: 70, flexShrink: 0 }}>{label}</span>
                          <div style={{ flex: 1, height: 5, background: "#f3f4f6", borderRadius: 3 }}>
                            <div style={{ height: "100%", width: `${(parseFloat(val)/5)*100}%`, background: G, borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#374151", width: 24, textAlign: "right" }}>{val}</span>
                        </div>
                      );
                    })}
                  </>
                ) : <div style={{ fontSize: 12, color: "#9ca3af" }}>Aucun avis pour le moment.</div>}
              </div>
              <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #f3f4f6", overflow: "hidden" }}>
                <iframe title="map-mini" width="100%" height="120" frameBorder="0" style={{ display: "block", border: 0 }}
                  src={`https://maps.google.com/maps?q=${encodeURIComponent((hotel.city || "") + ", Comores")}&z=13&output=embed`} />
                <div style={{ padding: "10px 14px" }}>
                  <div style={{ fontSize: 12, color: "#374151", fontWeight: 500, marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
                    <MapPin size={11} color="#9ca3af" />{hotel.city}, Comores
                  </div>
                  <a href={`https://maps.google.com/?q=${encodeURIComponent((hotel.city || "") + " Comores")}`} target="_blank" rel="noreferrer"
                    style={{ fontSize: 12, color: G, fontWeight: 600, textDecoration: "none" }}>Voir sur la carte →</a>
                </div>
              </div>
            </div>

            {/* ── SECTION ÉQUIPEMENTS PREMIUM ── */}
            {equipList.length > 0 && (
              <AmenitiesSection equipments={hotel.equipments} />
            )}

            {/* Chambres */}
            <div style={{ background: "#fff", borderRadius: 16, padding: "22px 24px", border: "1px solid #f3f4f6", marginBottom: 20 }}>
              <RoomsSection hotelId={hotel._id} roomsRef={roomsRef} slug={hotel.slug} onOpenDates={() => setMobileDatesOpen(true)} />
            </div>

            {/* Avis */}
            <div style={{ background: "#fff", borderRadius: 16, padding: "22px 24px", border: "1px solid #f3f4f6", marginBottom: 20 }}>
              <ReviewsSection hotelId={hotel._id} />
            </div>

            {/* Hôtels similaires */}
            <div style={{ background: "#fff", borderRadius: 16, padding: "22px 24px", border: "1px solid #f3f4f6", marginBottom: 32 }}>
              <SimilarHotels currentId={hotel._id} island={hotel.island} />
            </div>
          </div>

          {/* Sidebar */}
          <div className="hotel-sidebar" style={{ position: "sticky", top: 88 }}>
            <BookingSidebar hotel={hotel} roomsRef={roomsRef} />
          </div>
        </div>
      </div>

      {/* CTA hôteliers */}
      <div style={{ background: "#1a4a2e", padding: "32px 0" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z"/><path d="M9 21V12h6v9"/>
              </svg>
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#fff", marginBottom: 3 }}>Vous êtes hôtelier ? Rejoignez Komorotel</h3>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.7)" }}>Développez votre visibilité et accueillez plus de voyageurs.</p>
            </div>
          </div>
          <button onClick={() => navigate("/register-hotel")}
            style={{ background: "#fff", color: "#1a4a2e", border: "none", borderRadius: 10, padding: "11px 22px", fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
            Proposer mon établissement →
          </button>
        </div>
      </div>

      {/* Sticky bar mobile */}
      <div className="sticky-mobile" style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "#fff", borderTop: "1px solid #f3f4f6", padding: "12px 20px", display: "none", alignItems: "center", justifyContent: "space-between", zIndex: 90, boxShadow: "0 -4px 20px rgba(0,0,0,0.08)" }}>
        <div>
          <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 1 }}>À partir de</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>
            {hotel.minPrice}€<span style={{ fontSize: 11, fontWeight: 400, color: "#9ca3af" }}> /nuit</span>
          </div>
          {hotel.minPrice && <div style={{ fontSize: 10, color: "#9ca3af" }}>≈ {toKMF(hotel.minPrice)} KMF</div>}
          <button onClick={() => setMobileDatesOpen(true)}
            style={{ background: "none", border: "none", cursor: "pointer", color: G, fontSize: 12, fontWeight: 600, padding: 0 }}>
            {urlCheckIn
              ? `${new Date(urlCheckIn).toLocaleDateString("fr-FR",{day:"numeric",month:"short"})} → ${new Date(urlCheckOut).toLocaleDateString("fr-FR",{day:"numeric",month:"short"})}`
              : "Sélectionner des dates"}
          </button>
        </div>
        <button onClick={() => { if (!urlCheckIn) setMobileDatesOpen(true); else roomsRef.current?.scrollIntoView({ behavior: "smooth" }); }}
          style={{ background: G, color: "#fff", border: "none", borderRadius: 10, padding: "11px 22px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          Voir les disponibilités
        </button>
      </div>

      {/* Bottom sheet dates mobile */}
      {mobileDatesOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200 }}>
          <div onClick={() => setMobileDatesOpen(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} />
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "#fff", borderRadius: "20px 20px 0 0", paddingBottom: 32 }}>
            <div style={{ width: 40, height: 4, background: "#d1d5db", borderRadius: 2, margin: "14px auto 4px" }} />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px 8px" }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>Sélectionner vos dates</span>
              <button onClick={() => setMobileDatesOpen(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={20} color="#9ca3af" /></button>
            </div>
            <MobileDatePicker hotel={hotel} onClose={() => setMobileDatesOpen(false)} roomsRef={roomsRef} />
          </div>
        </div>
      )}

      <style>{`
        @keyframes shimmer { to { background-position: -200% 0; } }
        @media (max-width: 900px) {
          .sticky-mobile { display: flex !important; }
          .hotel-layout { grid-template-columns: 1fr !important; }
          .hotel-sidebar { display: none !important; }
          .hotel-info-grid { grid-template-columns: 1fr !important; }
          .hotel-container { padding-left: 16px !important; padding-right: 16px !important; max-width: 100vw !important; overflow-x: hidden !important; box-sizing: border-box !important; }
          .hotel-container img { max-width: 100% !important; }
          .hotel-container > * { max-width: 100% !important; overflow-x: hidden !important; }
          .hotel-layout > div { min-width: 0 !important; overflow-x: hidden !important; }
          .hotel-container p, .hotel-container span, .hotel-container h1, .hotel-container h2, .hotel-container h3 { word-break: break-word !important; overflow-wrap: break-word !important; }
        }
        .rdrCalendarWrapper { max-width: 100% !important; font-size: 11px !important; }
        .rdrMonth { width: calc(100vw - 64px) !important; min-width: 0 !important; }
        @media (max-width: 400px) {
          .rdrMonth { width: calc(100vw - 40px) !important; }
          .rdrDayNumber span { font-size: 11px !important; }
        }
      `}</style>
    </div>
  );
}